// Field-identical to SanityClient::POST_SUMMARY_PROJECTION (Rails): the blog
// index is SSR-seeded from the Rails copy, and a field only one side asks for
// is either missing from the first paint or a hydration mismatch.
export const POST_SUMMARY_PROJECTION = `{
  _id,
  "slug": slug.current,
  title,
  excerpt,
  coverImage{..., asset->{_id, _ref}},
  categories,
  publishedAt,
  readingMinutes,
  "featured": coalesce(featured, false),
  _updatedAt,
  "author": author->{
    _id,
    name,
    role,
    "slug": slug.current,
    picture{..., asset->{_id, _ref}}
  },
  "noIndex": coalesce(noIndex, false)
}`

export const POST_FULL_PROJECTION = `{
  _id,
  "slug": slug.current,
  title,
  excerpt,
  coverImage{..., asset->{_id, _ref}},
  categories,
  publishedAt,
  readingMinutes,
  "featured": coalesce(featured, false),
  _updatedAt,
  "author": author->{
    _id,
    name,
    role,
    "slug": slug.current,
    picture{..., asset->{_id, _ref}}
  },
  body,
  seoTitle,
  seoDescription,
  ogImage{..., asset->{_id, _ref}},
  "noIndex": coalesce(noIndex, false)
}`

export const ALL_POSTS_QUERY = `*[_type == "post" && defined(publishedAt) && publishedAt <= now()] | order(publishedAt desc) ${POST_SUMMARY_PROJECTION}`

export const POST_BY_SLUG_QUERY = `*[_type == "post" && slug.current == $slug && defined(publishedAt) && publishedAt <= now()][0] ${POST_FULL_PROJECTION}`

export const RELATED_POSTS_QUERY = `*[
  _type == "post"
  && slug.current != $slug
  && defined(publishedAt)
  && publishedAt <= now()
  && count(categories[@ in $categories]) > 0
] | order(count(categories[@ in $categories]) desc, publishedAt desc) [0...$limit] ${POST_SUMMARY_PROJECTION}`

// Both field-identical to SanityClient::AUTHOR_SUMMARY_PROJECTION /
// AUTHOR_FULL_PROJECTION (Rails), which SSR-seed /authors and /authors/:slug.
const AUTHOR_SUMMARY_PROJECTION = `{
  _id,
  "slug": slug.current,
  name,
  role,
  shortBio,
  picture{..., asset->{_id, _ref}},
  _updatedAt
}`

const AUTHOR_FULL_PROJECTION = `{
  _id,
  "slug": slug.current,
  name,
  role,
  shortBio,
  picture{..., asset->{_id, _ref}},
  longBio,
  socialLinks[]{label, url},
  "posts": *[_type == "post" && references(^._id) && defined(publishedAt) && publishedAt <= now()] | order(publishedAt desc) ${POST_SUMMARY_PROJECTION}
}`

export const ALL_AUTHORS_QUERY = `*[_type == "author" && defined(slug.current)] | order(name asc) ${AUTHOR_SUMMARY_PROJECTION}`

export const AUTHOR_BY_SLUG_QUERY = `*[_type == "author" && slug.current == $slug][0] ${AUTHOR_FULL_PROJECTION}`

// Field-identical to SanityClient::LANGUAGE_PAGE_FULL_PROJECTION (Rails), which
// SSR-seeds these pages. Testimonials are deliberately not fetched (#771).
const LANGUAGE_PAGE_PROJECTION = `{
  _id,
  "slug": slug.current,
  language,
  title,
  hero{
    heading,
    subheading,
    ctaLabel,
    ctaHref,
    image{..., asset->{_id, _ref}}
  },
  intro{
    heading,
    body,
    image{..., asset->{_id, _ref}}
  },
  features[]{icon, title, description},
  steps[]{title, description},
  faq[]{question, answer},
  finalCta{heading, subheading, ctaLabel, ctaHref},
  seoTitle,
  seoDescription,
  ogImage{..., asset->{_id, _ref}},
  "noIndex": coalesce(noIndex, false)
}`

export const TEACH_LANGUAGE_PAGE_QUERY = `*[_type == "teachLanguagePage" && slug.current == $slug][0] ${LANGUAGE_PAGE_PROJECTION}`

export const LEARN_LANGUAGE_PAGE_QUERY = `*[_type == "learnLanguagePage" && slug.current == $slug][0] ${LANGUAGE_PAGE_PROJECTION}`

// Locale-aware variant. `en` (or no locale) selects the English base doc, which
// has no `locale` field; any other locale selects the imported translation.
// Mirrors the Rails SanityClient so client + server stay consistent.
export const languagePageQuery = (variant: 'learn' | 'teach', locale?: string) => {
  const type = variant === 'teach' ? 'teachLanguagePage' : 'learnLanguagePage'
  const localeFilter = locale && locale !== 'en' ? 'locale == $locale' : '!defined(locale)'
  return `*[_type == "${type}" && slug.current == $slug && ${localeFilter}][0] ${LANGUAGE_PAGE_PROJECTION}`
}
